import { useCallback, useEffect, useState } from 'react'
import { classNames } from 'utils/dom-helpers'

import Translation from 'components/Translation'
import ConfirmDialogModal from '../ConfirmDialog'
import { ModalDataAttributes } from '../types'
import GroupClassRowForm from './GroupClassRowForm'
import LocationSelector from 'components/DropdownSelectors/LocationSelector'
import { ServiceSelector } from 'components/DropdownSelectors/ServiceSelector'
import { Button, ButtonGroup } from '@progress/kendo-react-buttons'
import {
    strToDate,
    toHHMM,
    toYmd,
} from '../../../utils/string-localization-helper'
import { days_of_week } from '../Class/ClassItem'
import { Switch } from '@headlessui/react'
import { ServiceApiTypeEnum } from 'types/service'
import { useTenantContext } from 'context/TenantContext'
import { LocationItem } from 'types/location'
import { putApiRequest } from 'utils/fetch-helper'
import { useAuth } from 'context/AuthContext'
import AlertError from 'components/AlertPrompt/AlertError'
import { ErrorMessage } from 'types/error-message'
import GenericError from 'components/GenericError/GenericError'
import {
    DEFAULT_ERROR_STATE,
    IError,
    useAppContext,
} from 'context/AppContext'
import { getTranslation } from 'utils/language-helper'

export function BlankOffer({
    onNext,
    showOfferListForm,
}: {
    onNext(): void
    showOfferListForm(): void
}) {
    const { ServiceModal } = useAuth()
    const { attributes } = ServiceModal
    return (
        <div className="rounded-xl relative flex-1">
            <div
                className={classNames(
                    'justify-center',
                    'overflow-auto flex-1 flex flex-col'
                )}
            >
                <>
                    <div className="rounded-full bg-gray-200 w-20 h-20 inline-block leading-loose text-center self-center align-middle">
                        <i className="text-gray-400 feather feather-briefcase text-4xl leading-loose" />
                    </div>
                    <div className="text-gray-400 text-xl font-display block mt-4 mx-auto ">
                        <Translation content_key="blank_offers_title_start" />
                        {attributes && attributes.name}
                        <Translation content_key="blank_offers_title_end" />
                    </div>
                    <Translation
                        render_as="div"
                        className="text-gray-400 block mx-auto mb-3"
                        content_key="blank_offers_body"
                    />
                    <button
                        className="bg-primary w-auto mx-auto font-sans text-lg px-5 py-1 my-3 text-white rounded-lg"
                        type="button"
                        onClick={showOfferListForm}
                    >
                        <Translation content_key="offer_classes_button" />
                    </button>
                    <button
                        className="border border-gray-400 w-48 mx-auto font-sans text-lg px-5 py-1 rounded-lg"
                        type="button"
                        onClick={onNext}
                    >
                        <Translation content_key="skip_button" />
                    </button>
                </>
            </div>
        </div>
    )
}

function LocalCopyWarning() {
    const [show_tool_tip, toggleShowTooltip] = useState<boolean>(false)
    return (
        <div
            onClick={() => toggleShowTooltip(true)}
            onMouseEnter={() => toggleShowTooltip(true)}
            onMouseLeave={() => toggleShowTooltip(false)}
        >
            <img src="/images/Warning.svg" alt="warning" />
            <div
                className={classNames(
                    'shadow-md p-2 rounded-t-sm z-30 absolute bg-slate-100',
                    show_tool_tip ? 'visible' : 'invisible'
                )}
                style={{
                    border: '1px solid lightGray',
                }}
            >
                <p>
                    <Translation content_key="unsaved_class" />
                </p>
            </div>
        </div>
    )
}

function ServiceModalOfferClasses({
    onAttributesChanged,
    removeItem,
}: {
    onAttributesChanged(u: ModalDataAttributes, idx: number): void
    removeItem(idx: number): void
}) {
    const { ServiceModal } = useAuth()
    const { attributes, setAttributes } = ServiceModal
    const { setGlobalLoading, translations } = useAppContext()
    const { all_staff, staff, locations, getGroupClasses } = useTenantContext()
    const api_error =
        attributes && (attributes.api_error as Record<string, string>)
    const is_series = attributes?.service_type === ServiceApiTypeEnum.SERIES

    const [show_confirmation, setShowConfirmation] = useState(false)
    const [show_completed_series, toggleShowCompletedSeries] =
        useState<boolean>(false)
    const [current_class, setCurrentClass] = useState(-1)
    const [location_items, setLocationItems] = useState<
        Record<string, string>[]
    >([])
    const [selected_locations, setSelectedLocations] = useState<string[]>(
        []
    )
    const [active_staff_items, setActiveStaffItems] = useState<Record<string, string>[]>(
        []
    )
    const [all_staff_items, setAllStaffItems] = useState<Record<string, string>[]>(
        []
    )
    const [selected_staffs, setSelectedStaffs] = useState<string[]>([])
    const [has_local, toggleHasLocal] = useState<boolean>(false)
    const [isRecurringFilter, setIsRecurringFilter] = useState(true)
    const [staff_id_filter, setStaffIdFilter] = useState<string[]>([])
    const [error, setError] = useState<IError>(DEFAULT_ERROR_STATE)
    let group_classes =
        attributes && (attributes.group_classes as ModalDataAttributes[])

    function duplicateRow(idx: number, is_add = false) {
        if (group_classes && group_classes[idx]) {
            const group_class = group_classes[idx]
            const date = is_add ? new Date() : group_class.date
            const time = toHHMM(date as Date)
            group_classes.push({
                ...group_classes[idx],
                id: '',
                setting_id: '',
                time,
                date,
                availabilities: is_add
                    ? [{ date: '', start_time: time }]
                    : (
                        group_class?.availabilities as ModalDataAttributes[]
                    )?.slice(),
                day_of_week:
                    days_of_week[
                    new Date(toYmd(group_class.date as Date)).getDay()
                    ],
            })
            setAttributes({
                ...attributes,
                group_classes,
            })
        }
    }

    function addNewClass() {
        const index = (group_classes && group_classes?.length - 1) || 0

        duplicateRow(index, true)
        toggleHasLocal(true)
    }

    function onConfirmation() {
        if (is_series) {
            handleDeleteSeries(group_classes?.[current_class].id as string)
        } else {
            removeItem(current_class)
        }

        setShowConfirmation(false)
    }

    useEffect(() => {
        if (staff && staff.length > 0) {
            setActiveStaffItems(
                staff.map((s: any) => ({
                    id: s.id,
                    name: s.user.first_name + ' ' + s.user.last_name,
                }))
            )
        }
    }, [staff])

    useEffect(() => {
        if (all_staff && all_staff.length > 0) {
            setAllStaffItems(
                all_staff.map((s: any) => ({
                    id: s.id,
                    name: s.user.first_name + ' ' + s.user.last_name,
                }))
            )
        }
    }, [all_staff])

    useEffect(() => {
        setSelectedStaffs(all_staff_items.map((item) => item.name))
    }, [all_staff_items])

    useEffect(() => {
        setStaffIdFilter(
            all_staff_items
                .filter((item) => selected_staffs.includes(item.name))
                .map((item) => item.id)
        )
    }, [selected_staffs])

    useEffect(() => {
        if (locations && locations.length > 0) {
            setLocationItems(
                locations.map((item: LocationItem) => ({
                    id: item.id,
                    text: item.name,
                }))
            )
        }
    }, [locations])

    useEffect(() => {
        setSelectedLocations(location_items.map((item) => item.id))
    }, [location_items])

    const getLocationFilter = useCallback(
        () => (
            <LocationSelector
                className="z-10"
                items={location_items}
                allow_multiple={false}
                setSelectedLocations={setSelectedLocations}
                selectedLocations={selected_locations}
            />
        ),
        [location_items, selected_locations]
    )

    const getInstructorFilter = useCallback(
        () => (
            <ServiceSelector
                className="absolute z-10"
                items={all_staff_items}
                setSelectedServices={setSelectedStaffs}
                selectedServices={selected_staffs}
                title="Staff"
            />
        ),
        [all_staff, selected_staffs]
    )

    const getHeaderContent = useCallback(() => {
        return (
            <>
                {' '}
                {is_series ? (
                    <>
                        <Translation
                            render_as="h2"
                            content_key="common.offer_class_series_for"
                            className="inline-block"
                        />
                        &nbsp;
                        <h2 className="text-primary-light inline-block">
                            {attributes && attributes.name}
                        </h2>
                        <div className="float-right text-base">
                            <Translation
                                render_as="label"
                                content_key="common.class_duration_with_colon"
                            />
                            &nbsp;
                            <Translation
                                render_as="label"
                                content_key="common.x_minutes"
                                content_values={{
                                    mins_val: attributes.duration as string,
                                }}
                                className="font-bold"
                            />
                        </div>
                        <Translation
                            render_as="p"
                            className="text-base font-display text-gray-400 mt-2"
                            content_key="common.offer_class_series_desc"
                        />
                    </>
                ) : (
                    <>
                        <Translation content_key="offer_class_title_1" />
                        &nbsp;
                        <span className="text-primary-light">
                            {attributes && attributes.name}
                        </span>
                        &nbsp;
                        <Translation content_key="offer_class_title_2" />
                        <Translation
                            render_as="div"
                            className="text-base text-gray-400 mt-2"
                            content_key="offer_class_body"
                        />
                    </>
                )}
            </>
        )
    }, [attributes?.service_type, attributes?.name])

    const handleRecurring = (value: boolean) => {
        setIsRecurringFilter(value)
    }

    const del = (index: number) => {
        if (group_classes) {
        }
    }

    async function handleDeleteSeries(id: string) {
        setGlobalLoading(true)
        let res = await putApiRequest(
            `/v1/group_classes/series/soft-delete/${id}`
        )
        if (res?.ok) {
            res = await getGroupClasses()
            if (res?.ok) {
                setGlobalLoading(false)
            }
        } else {
            setError({ tag: res?.tag, message: res?.message })
            setGlobalLoading(false)
        }
    }

    return (
        <div
            className="relative flex flex-col w-full px-3"
        >
            <div className="text-primary text-lg font-medium font-display">
                {getHeaderContent()}
            </div>
            <div className="flex gap-3 items-center mt-5 w-full relative">
                <div>{getLocationFilter()}</div>
                <div>{getInstructorFilter()}</div>
                {is_series ? (
                    <div className="flex items-center right-0 absolute">
                        <Switch
                            checked={show_completed_series}
                            onChange={() =>
                                toggleShowCompletedSeries(
                                    !show_completed_series
                                )
                            }
                            className={classNames(
                                show_completed_series
                                    ? 'bg-primary'
                                    : 'bg-gray-300',
                                `block relative inline-flex flex-shrink-0 h-5 w-10
                            border-2 border-transparent rounded-full cursor-pointer
                            transition-colors ease-in-out duration-200 focus:outline-none 
                            focus-visible:ring-2  focus-visible:ring-white focus-visible:ring-opacity-75`
                            )}
                        >
                            <span
                                aria-hidden="true"
                                className={classNames(
                                    show_completed_series
                                        ? 'translate-x-5'
                                        : 'translate-x-0',
                                    `pointer-events-none inline-block h-4 w-4 rounded-full 
                                    bg-white shadow-lg transform ring-0 transition ease-in-out duration-300`
                                )}
                            />
                        </Switch>
                        <Translation
                            content_key="common.display_completed_series"
                            render_as="label"
                            className="my-auto text-base font-[450] ml-3"
                        />
                    </div>
                ) : (
                    <div>
                        <ButtonGroup className="bg-primary-light circular-light w-auto">
                            <Button
                                className="max-w-md"
                                key="recurring"
                                togglable={true}
                                selected={isRecurringFilter}
                                onClick={() => handleRecurring(true)}
                            >
                                <Translation content_key="recurring" />
                            </Button>
                            <Button
                                className="max-w-md"
                                key="oneTime"
                                togglable={true}
                                selected={!isRecurringFilter}
                                onClick={() => handleRecurring(false)}
                            >
                                <Translation content_key="one_time" />
                            </Button>
                        </ButtonGroup>
                    </div>
                )}
            </div>
            <div className="my-6 flex-1">
                <div
                    className={classNames(
                        'justify-start',
                        'flex-1 flex flex-col gap-3'
                    )}
                >
                    <div className="flex gap-2">
                        <div style={{ width: '2.2%' }}></div>
                        {is_series && (
                            <Translation
                                render_as="span"
                                className="flex flex-auto pl-2 font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-40"
                                content_key="common.series_class_name"
                            />
                        )}
                        <Translation
                            render_as="span"
                            className="flex flex-auto pl-2 font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-40"
                            content_key="location_col_header"
                        />

                        <Translation
                            render_as="span"
                            className="flex flex-auto pl-1 font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-40"
                            content_key="location_space"
                        />

                        <Translation
                            render_as="span"
                            className="flex flex-auto pl-1 font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-40"
                            content_key="instructor_col_header"
                        />
                        {!is_series && (
                            <>
                                <Translation
                                    render_as="span"
                                    className="flex flex-auto pl-1 font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-36"
                                    content_key="date_col_header"
                                />
                                <Translation
                                    render_as="span"
                                    className="flex flex-auto pl-1 font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-36"
                                    content_key="time_col_header"
                                />
                            </>
                        )}

                        <Translation
                            render_as="span"
                            className="flex flex-auto pl-1 font-semibold tracking-wider font-display text-gray-500 text-xs uppercase w-44"
                            content_key="recurrence_col_header"
                        />
                    </div>
                    <div>
                        {group_classes?.map(
                            (
                                group_class: ModalDataAttributes,
                                idx: number
                            ) => {
                                const is_local = !group_class.id
                                let result = (
                                    <div
                                        className="flex items-center "
                                        key={'group_class_row_' + idx}
                                        id={'group_class_row_' + idx}
                                    >
                                        <div style={{ width: '3%' }}>
                                            {is_local && (
                                                <LocalCopyWarning />
                                            )}
                                        </div>
                                        <div className="bg-primary-lighter bg-opacity-40 flex flex-col gap-2 max-h-large overflow-hidden text-sm w-full">
                                            <GroupClassRowForm
                                                group_class={group_class}
                                                idx={idx}
                                                is_series={is_series}
                                                staff_items={group_class.id && group_class.is_recurring ? all_staff_items : active_staff_items}
                                                open_series={
                                                    idx + 1 ==
                                                    group_classes?.length
                                                }
                                                onAttributesChanged={
                                                    onAttributesChanged
                                                }
                                                duplicateRow={duplicateRow}
                                                removeHandler={(
                                                    is_local: boolean
                                                ) => {
                                                    if (!is_local) {
                                                        setShowConfirmation(
                                                            true
                                                        )
                                                        setCurrentClass(idx)
                                                        del(idx)
                                                    } else {
                                                        removeItem(idx)
                                                        toggleHasLocal(
                                                            false
                                                        )
                                                    }
                                                }}
                                                setError={setError}
                                                toggleHasLocal={toggleHasLocal}
                                            />
                                        </div>
                                    </div>
                                )
                                if (is_local) {
                                    return result
                                } else {
                                    const availabilities =
                                        group_class.availabilities as ModalDataAttributes[]
                                    if (
                                        selected_locations.includes(
                                            group_class.location as string
                                        ) &&
                                        staff_id_filter.includes(
                                            group_class.staff as string
                                        ) &&
                                        (group_class.is_recurring ==
                                            isRecurringFilter ||
                                            (is_series &&
                                                (show_completed_series ||
                                                    !availabilities ||
                                                    availabilities?.length ==
                                                    0 ||
                                                    !availabilities?.[
                                                        availabilities?.length -
                                                        1 || 0
                                                    ].end_time ||
                                                    strToDate(
                                                        availabilities?.[
                                                            availabilities?.length -
                                                            1 || 0
                                                        ].date as Date,
                                                        availabilities?.[
                                                            availabilities?.length -
                                                            1 || 0
                                                        ]
                                                            ?.end_time as string
                                                    ) >= new Date())))
                                    ) {
                                        return result
                                    }
                                }
                                return null
                            }
                        )}
                    </div>
                    {!has_local && (
                        <button
                            className="flex justify-center mt-3 border-dashed border-2 border-stone-400 h-10 items-center text-stone-400 rounded"
                            style={{ marginLeft: '3%', marginRight: 0 }}
                            onClick={addNewClass}
                        >
                            <i className="feather-plus text-lg" />
                            <Translation
                                content_key={
                                    is_series
                                        ? 'common.add_new_class_series'
                                        : 'common.add_class'
                                }
                            />
                        </button>
                    )}
                </div>
            </div>
            <div>
                {api_error && api_error.message ? (
                    <div className="text-sm text-red-700">
                        {api_error.message}
                    </div>
                ) : (
                    ''
                )}
            </div>
            {show_confirmation && (
                <ConfirmDialogModal
                    prompt_message={getTranslation(
                        is_series
                            ? 'services.delete_series'
                            : 'services.delete_group_class',
                        translations
                    )}
                    onCancel={() => {
                        setShowConfirmation(false)
                    }}
                    onConfirm={() => onConfirmation()}
                />
            )}
            {!!error?.message?.length && (
                <GenericError
                    error_message={error.message}
                    onCloseModal={() => {
                        setError(DEFAULT_ERROR_STATE)
                    }}
                    tag={error?.tag}
                />
            )}
        </div>
    )
}

export default ServiceModalOfferClasses
